import { Link } from '@tanstack/react-router';
import {
  DashboardDataTableColumnKind,
  buildDashboardDataTableColumns,
  dashboardTableTextActionClassName,
} from '@shigo-ui/dashboard-ui';

import { formatDisplayDate } from '../../../../../lib/format';
import { dashboardT } from '../../../../../lib/messages';
import type { AssignedAgency } from '../../../../auth/agencyAccess';
import { agencyDetailsLink, brandDetailsLink } from '../../../hierarchyLinks';
import type { Brand } from '../../model';
import { BrandActions } from './BrandActions';

const brandDateFormat = { day: '2-digit', month: '2-digit', year: 'numeric' } as const;
const brandTimeFormat = { hour: '2-digit', hour12: false, minute: '2-digit' } as const;

type BrandsTableColumnsOptions = {
  /** Agency workspace when rendered inside `/a/$agencyId`. */
  scopeAgency: AssignedAgency | undefined;
  /** Agency whose details page embeds the table. */
  parentAgency: AssignedAgency | undefined;
  agencyByBrandId: ReadonlyMap<string, AssignedAgency>;
  canWrite: boolean;
  onEdit: (brand: Brand) => void;
};

export function buildBrandsTableColumns({
  scopeAgency,
  parentAgency,
  agencyByBrandId,
  canWrite,
  onEdit,
}: BrandsTableColumnsOptions) {
  const scopeAgencyId = scopeAgency?.id;
  const fixedAgency = scopeAgency ?? parentAgency;
  return buildDashboardDataTableColumns<Brand>([
    {
      id: 'name',
      kind: DashboardDataTableColumnKind.Text,
      header: dashboardT('brands.name'),
      value: (brand) => (
        <Link
          {...brandDetailsLink(brand.id, scopeAgencyId)}
          className={dashboardTableTextActionClassName()}
        >
          {brand.name}
        </Link>
      ),
      headerClassName: 'pl-4',
      className: 'pl-4 font-medium',
    },
    {
      id: 'agency',
      hidden: Boolean(fixedAgency),
      kind: DashboardDataTableColumnKind.Text,
      header: dashboardT('agencyAssignment.label'),
      value: (brand) => {
        const agency = fixedAgency ?? agencyByBrandId.get(brand.id);
        return agency ? (
          <Link
            {...agencyDetailsLink(agency.id, scopeAgencyId)}
            className={dashboardTableTextActionClassName()}
          >
            {agency.name}
          </Link>
        ) : (
          '-'
        );
      },
    },
    {
      id: 'industry',
      kind: DashboardDataTableColumnKind.Text,
      header: dashboardT('brands.industry'),
      value: (brand) => brand.industry,
    },
    {
      id: 'createdOn',
      kind: DashboardDataTableColumnKind.Custom,
      header: dashboardT('brands.createdOn'),
      headerClassName: 'w-28',
      className: 'w-28 whitespace-nowrap',
      cell: (brand) => (
        <span title={formatDisplayDate(brand.createdOn, { ...brandTimeFormat, fallback: '' })}>
          {formatDisplayDate(brand.createdOn, brandDateFormat)}
        </span>
      ),
    },
    {
      id: 'updatedOn',
      kind: DashboardDataTableColumnKind.Custom,
      header: dashboardT('brands.updatedOn'),
      headerClassName: 'w-28',
      className: 'w-28 whitespace-nowrap',
      cell: (brand) => (
        <span title={formatDisplayDate(brand.updatedOn, { ...brandTimeFormat, fallback: '' })}>
          {formatDisplayDate(brand.updatedOn, brandDateFormat)}
        </span>
      ),
    },
    {
      id: 'actions',
      kind: DashboardDataTableColumnKind.Actions,
      hidden: !canWrite,
      cell: (brand) => <BrandActions brand={brand} onEdit={onEdit} />,
    },
  ]);
}
